import { useState, useCallback } from "react";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { getPageRoles } from "src/pages/CMS/utils/roles";
import { normalizeUrl, isUrlValid } from "../components/urlValidation";
import { isHomePage } from "../components/utils";

type RootProps = Record<string, unknown>;

type DialogState<T> = {
    open: boolean;
    page: T | null;
};

export type UsePageDialogsArgs = {
    pageType: "CMS" | "PDF";
    onUpdateMeta?: (
        slug: string,
        payload: {
            url?: string;
            sectors?: string[] | null;
            allowedRoles?: string[];
            status?: "active" | "draft";
            title?: string;
            description?: string;
            downloadOnlyOnSsqValidated?: boolean;
            score?: string | string[];
            entity?: string | string[];
        },
    ) => Promise<unknown> | void;
    onUpdateRoles?: (slug: string, roles: string[]) => void;
    onDuplicatePage?: (source: PuckPage, payload: { title: string; url: string }) => Promise<unknown> | void;
    isCreating?: boolean;
};

const normalizeStringList = (v: unknown): string[] => {
    if (Array.isArray(v)) {
        return v.filter((x): x is string => typeof x === "string" && x.trim() !== "");
    }
    if (typeof v === "string") {
        const vv = v.trim();
        return vv === "" ? [] : [vv];
    }
    return [];
};

const getRootProps = (page: PuckPage): RootProps => {
    try {
        const obj: unknown = page.content ? JSON.parse(page.content) : {};
        if (!obj || typeof obj !== "object") return {};
        const root = (obj as { root?: unknown }).root;
        if (!root || typeof root !== "object") return {};
        const props = (root as { props?: unknown }).props;
        return props && typeof props === "object" ? (props as RootProps) : {};
    } catch {
        return {};
    }
};

const getDownloadOnlyOnSsqValidated = (rootProps: RootProps): boolean => {
    const value = rootProps.downloadOnlyOnSsqValidated;
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value !== "false";
    if (typeof value === "number") return value !== 0;
    return true;
};

export const usePageDialogs = (args: UsePageDialogsArgs) => {
    const { pageType, onUpdateMeta, onUpdateRoles, onDuplicatePage, isCreating } = args;

    const [rolesDialog, setRolesDialog] = useState<DialogState<PuckPage>>({ open: false, page: null });
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [isRolesUpdating, setIsRolesUpdating] = useState(false);

    const [settingsDialog, setSettingsDialog] = useState<DialogState<PuckPage>>({ open: false, page: null });
    const [settingsTitle, setSettingsTitle] = useState("");
    const [settingsDescription, setSettingsDescription] = useState("");
    const [settingsUrl, setSettingsUrl] = useState("");
    const [settingsRoles, setSettingsRoles] = useState<string[]>([]);
    const [settingsActive, setSettingsActive] = useState(true);
    const [settingsSectorIds, setSettingsSectorIds] = useState<number[]>([]);
    const [settingsScore, setSettingsScore] = useState<string[]>([]);
    const [settingsEntity, setSettingsEntity] = useState<string[]>([]);
    const [settingsDownloadOnlyOnSsqValidated, setSettingsDownloadOnlyOnSsqValidated] = useState(true);
    const [isSettingsSaving, setIsSettingsSaving] = useState(false);

    const [scoreCompanyDialog, setScoreCompanyDialog] = useState<DialogState<PuckPage>>({ open: false, page: null });
    const [selectedScore, setSelectedScore] = useState<string[]>([]);
    const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
    const [isScoreCompanySaving, setIsScoreCompanySaving] = useState(false);

    const [duplicateDialog, setDuplicateDialog] = useState<DialogState<PuckPage>>({ open: false, page: null });
    const [isDuplicating, setIsDuplicating] = useState(false);

    const openRolesDialog = useCallback((page: PuckPage) => {
        setRolesDialog({ open: true, page });
        setSelectedRoles(getPageRoles(page));
    }, []);

    const closeRolesDialog = useCallback(() => {
        setRolesDialog({ open: false, page: null });
    }, []);

    const saveRoles = useCallback(() => {
        if (!onUpdateRoles || !rolesDialog.page) return;
        setIsRolesUpdating(true);
        try {
            onUpdateRoles(rolesDialog.page.slug, selectedRoles);
        } finally {
            closeRolesDialog();
            setIsRolesUpdating(false);
        }
    }, [onUpdateRoles, rolesDialog.page, selectedRoles, closeRolesDialog]);

    const openSettingsDialog = useCallback((page: PuckPage) => {
        const props = getRootProps(page);
        setSettingsDialog({ open: true, page });
        setSettingsTitle(page.title);
        setSettingsDescription(typeof props.description === "string" ? props.description : "");
        setSettingsRoles(getPageRoles(page));
        setSettingsActive(page.status === "active");
        const fallbackUrl = isHomePage(page.slug) ? "/" : `/${page.slug}`;
        setSettingsUrl(page.url || fallbackUrl);
        setSettingsScore(normalizeStringList(props.score));
        setSettingsEntity(normalizeStringList(props.entity));
        setSettingsDownloadOnlyOnSsqValidated(getDownloadOnlyOnSsqValidated(props));
        const iris = (page.sectors as string[] | undefined) || [];
        const ids = iris
            .map((iri) => {
                const m = iri.match(/\/(\d+)$/);
                return m ? Number(m[1]) : null;
            })
            .filter((x): x is number => x !== null);
        setSettingsSectorIds(ids);
    }, []);

    const closeSettingsDialog = useCallback(() => {
        setSettingsDialog({ open: false, page: null });
    }, []);

    const saveSettings = useCallback(async () => {
        if (!settingsDialog.page || !onUpdateMeta) {
            closeSettingsDialog();
            return;
        }
        const normalized = normalizeUrl(settingsUrl);
        if (!isUrlValid(normalized)) return;
        setIsSettingsSaving(true);
        try {
            await Promise.resolve(
                onUpdateMeta(settingsDialog.page.slug, {
                    title: settingsTitle,
                    description: pageType === "PDF" ? settingsDescription : undefined,
                    downloadOnlyOnSsqValidated: pageType === "PDF" ? settingsDownloadOnlyOnSsqValidated : undefined,
                    allowedRoles: settingsRoles,
                    status: settingsActive ? "active" : "draft",
                    url: normalized,
                    sectors:
                        settingsSectorIds.length > 0 ?
                            settingsSectorIds.map((id) => `/api/organization_sectors/${id}`)
                        :   [],
                    score: pageType === "PDF" ? settingsScore : undefined,
                    entity: pageType === "PDF" ? settingsEntity : undefined,
                }),
            );
        } finally {
            setIsSettingsSaving(false);
            closeSettingsDialog();
        }
    }, [
        onUpdateMeta,
        pageType,
        settingsActive,
        settingsDescription,
        settingsDownloadOnlyOnSsqValidated,
        settingsEntity,
        settingsDialog.page,
        settingsRoles,
        settingsScore,
        settingsSectorIds,
        settingsTitle,
        settingsUrl,
        closeSettingsDialog,
    ]);

    const openScoreCompanyDialog = useCallback((page: PuckPage) => {
        const props = getRootProps(page);
        setScoreCompanyDialog({ open: true, page });
        setSelectedScore(normalizeStringList(props.score));
        setSelectedEntity(normalizeStringList(props.entity));
    }, []);

    const closeScoreCompanyDialog = useCallback(() => {
        setScoreCompanyDialog({ open: false, page: null });
    }, []);

    const saveScoreCompany = useCallback(async () => {
        if (!scoreCompanyDialog.page || !onUpdateMeta) return;
        setIsScoreCompanySaving(true);
        try {
            await Promise.resolve(
                onUpdateMeta(scoreCompanyDialog.page.slug, {
                    score: selectedScore,
                    entity: selectedEntity,
                }),
            );
            closeScoreCompanyDialog();
        } finally {
            setIsScoreCompanySaving(false);
        }
    }, [onUpdateMeta, scoreCompanyDialog.page, selectedScore, selectedEntity, closeScoreCompanyDialog]);

    const openDuplicateDialog = useCallback((page: PuckPage) => {
        setDuplicateDialog({ open: true, page });
    }, []);

    const closeDuplicateDialog = useCallback(() => {
        setDuplicateDialog({ open: false, page: null });
    }, []);

    const confirmDuplicate = useCallback(
        async ({ title, url }: { title: string; url: string }) => {
            if (!duplicateDialog.page || !onDuplicatePage) return;
            try {
                setIsDuplicating(true);
                await Promise.resolve(onDuplicatePage(duplicateDialog.page, { title, url }));
            } finally {
                setIsDuplicating(false);
            }
            closeDuplicateDialog();
        },
        [duplicateDialog.page, onDuplicatePage, closeDuplicateDialog],
    );

    return {
        rolesDialog: {
            open: rolesDialog.open && !!rolesDialog.page,
            pageTitle: rolesDialog.page?.title || "",
            selectedRoles,
            setSelectedRoles,
            onClose: closeRolesDialog,
            onSave: saveRoles,
            isSaving: isRolesUpdating,
        },
        scoreCompanyDialog: {
            open: scoreCompanyDialog.open && !!scoreCompanyDialog.page,
            onClose: closeScoreCompanyDialog,
            selectedScore,
            setSelectedScore,
            selectedEntity,
            setSelectedEntity,
            onSave: saveScoreCompany,
            isSaving: isScoreCompanySaving,
        },
        settingsDialog: {
            page: settingsDialog.page,
            open: settingsDialog.open,
            pageType,
            settingsTitle,
            settingsDescription,
            settingsUrl,
            settingsRoles,
            settingsActive,
            settingsSectorIds,
            settingsScore,
            settingsEntity,
            settingsDownloadOnlyOnSsqValidated,
            disabled: isSettingsSaving,
            saving: isSettingsSaving,
            urlError: !isUrlValid(normalizeUrl(settingsUrl)),
            urlHelperText: !isUrlValid(normalizeUrl(settingsUrl)) ? "URL is invalid or reserved" : "",
            saveDisabled: !isUrlValid(normalizeUrl(settingsUrl)),
            onChangeTitle: setSettingsTitle,
            onChangeDescription: setSettingsDescription,
            onChangeUrl: setSettingsUrl,
            onChangeActive: setSettingsActive,
            onChangeRoles: setSettingsRoles,
            onChangeSectorIds: (ids: number[]) => setSettingsSectorIds(ids),
            onChangeScore: setSettingsScore,
            onChangeEntity: setSettingsEntity,
            onChangeDownloadOnlyOnSsqValidated: setSettingsDownloadOnlyOnSsqValidated,
            onClose: closeSettingsDialog,
            onSave: saveSettings,
        },
        duplicateDialog: {
            open: duplicateDialog.open,
            sourcePage: duplicateDialog.page,
            isLoading: isDuplicating || !!isCreating,
            onClose: closeDuplicateDialog,
            onConfirm: confirmDuplicate,
        },
        openRolesDialog,
        openSettingsDialog,
        openScoreCompanyDialog,
        openDuplicateDialog,
    };
};
